/**
 * Shared API success catalog.
 *
 * Controllers return these definitions. The web app compares `response.code`
 * with `apiSuccess.*.code` instead of matching English messages.
 */

export type ApiSuccessDefinition = {
  status: 200 | 201;
  code: string;
  message: string;
};

export const apiSuccess = {
  loggedIn: {
    status: 200,
    code: "logged_in",
    message: "Logged in successfully.",
  },
  accountCreated: {
    status: 201,
    code: "account_created",
    message: "Account created successfully.",
  },
  loggedOut: {
    status: 200,
    code: "logged_out",
    message: "Logged out successfully.",
  },
  authenticated: {
    status: 201,
    code: "auth_success",
    message: "User authenticated successfully.",
  },
  sessionValid: {
    status: 200,
    code: "session_is_valid",
    message: "Session is valid",
  },
  sessionRefreshed: {
    status: 200,
    code: "session_refreshed",
    message: "Session refreshed",
  },
  resetEmailSent: {
    status: 200,
    code: "email_sent",
    message: "If an account exists with this email, a password reset link has been sent.",
  },
  resetTokenVerified: {
    status: 200,
    code: "reset_token_verified",
    message: "Token verified successfully",
  },
  passwordReset: {
    status: 200,
    code: "password_reset_successfully",
    message: "Password has been reset successfully",
  },
  emailAlreadyVerified: {
    status: 200,
    code: "already_verified",
    message: "Email is already verified",
  },
  emailCodeSent: {
    status: 200,
    code: "sent_successfully",
    message: "Email verification code sent successfully",
  },
  emailCodeVerified: {
    status: 200,
    code: "email_code_verified",
    message: "Email verified successfully",
  },
  emailAvailable: {
    status: 200,
    code: "email_available",
    message: "Email is available.",
  },
  emailChanged: {
    status: 200,
    code: "email_verified",
    message: "Email verified successfully",
  },
  smsSent: {
    status: 200,
    code: "sms_sent",
    message: "SMS verification code is sent successfully.",
  },
  phoneVerified: {
    status: 200,
    code: "phone_verified",
    message: "Phone number is verified successfully.",
  },
  phoneAvailable: {
    status: 200,
    code: "phone_available",
    message: "The phone number is available.",
  },
  profileRetrieved: {
    status: 200,
    code: "profile_retrieved",
    message: "Profile retrieved successfully",
  },
  onboardingCompleted: {
    status: 200,
    code: "onboarding_completed",
    message: "Onboarding completed successfully",
  },
} as const satisfies Record<string, ApiSuccessDefinition>;

export type ApiSuccessKey = keyof typeof apiSuccess;
export type ApiSuccessCode = (typeof apiSuccess)[ApiSuccessKey]["code"];

const apiSuccessCodes = new Set<string>(
  Object.values(apiSuccess).map((success) => success.code),
);

export const isApiSuccessCode = (code: string): code is ApiSuccessCode =>
  apiSuccessCodes.has(code);

/** Builds the success envelope from a catalog entry. `data` is omitted when absent. */
export const ok = <T>(success: ApiSuccessDefinition, data?: T) => ({
  success: true as const,
  code: success.code,
  message: success.message,
  ...(data !== undefined ? { data } : {}),
});
